import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import supervisorService from '../../services/supervisorService';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';

export default function Approvals() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    fetchPendingLogs();
  }, []);

  const fetchPendingLogs = async () => {
    try {
      const response = await supervisorService.getPendingApprovals();
      setLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await supervisorService.approveLog(id);
      toast.success('Log approved');
      setLogs(logs.filter((log) => log.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!selectedLog || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await supervisorService.rejectLog(selectedLog.id, rejectReason);
      toast.success('Log rejected');
      setLogs(logs.filter((log) => log.id !== selectedLog.id));
      setShowRejectModal(false);
      setSelectedLog(null);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to reject');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Pending Approvals</h1>

      {logs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">All caught up!</h3>
          <p className="mt-2 text-gray-600">No pending approvals at this time.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => (
            <div key={log.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      {log.driver?.user?.first_name?.[0]?.toUpperCase() || 'D'}
                    </div>
                    <div>
                      <div className="font-medium">
                        {log.driver?.user?.first_name} {log.driver?.user?.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {format(new Date(log.log_date), 'MMMM d, yyyy')}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Shift:</span>{' '}
                      {log.shift_start && log.shift_end
                        ? `${log.shift_start} - ${log.shift_end}`
                        : '-'}
                    </div>
                    <div>
                      <span className="text-gray-500">Distance:</span>{' '}
                      {log.total_distance ? `${log.total_distance} km` : '-'}
                    </div>
                    <div>
                      <span className="text-gray-500">Trips:</span> {log.trips?.length || 0}
                    </div>
                    <div>
                      <span className="text-gray-500">Fuel:</span>{' '}
                      {log.fuel_used ? `${log.fuel_used} L` : '-'}
                    </div>
                  </div>

                  {log.notes && (
                    <div className="mt-3 text-sm text-gray-600">
                      <span className="font-medium">Notes:</span> {log.notes}
                    </div>
                  )}
                </div>

                <StatusBadge status={log.status} />
              </div>

              <div className="mt-4 pt-4 border-t flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedLog(log);
                    setShowRejectModal(true);
                  }}
                  className="btn btn-secondary"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(log.id)}
                  className="btn btn-primary"
                >
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Log Entry</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this log entry. The driver will be notified.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedLog(null);
                  setRejectReason('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleReject} className="btn btn-danger">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
